import CameraDetail from '@/Pages/CameraDetail/CameraDetail'
import type { IMapViewProps, IWebCOOSMapAsset } from '@/services/assets/types'
import { type IMap, LatLonBounds, MapLoader, type IGeoJSONLayerProps, type ILayerQueryEvent, type ILatLon, type ILatLonBounds, type IMapStateEvent, type IMapState } from '@axdspub/axiom-maps'
import { Loader, utils } from '@axdspub/axiom-ui-utilities'
import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import LatestImage from '../Media/LatestImage'
import { Link, useNavigate } from 'react-router'
import ImageLoader from '@/Components/Media/ImageLoader'
import { getLegendItemForAsset, legendItems, ucFirst } from '@/lib/utils'
import { useAtom } from 'jotai'
import isMobileAtom from '@/state/isMobileAtom'





const HoverView = ({
    properties,
    point
}: {
    properties: IWebCOOSMapAsset,
    point: { x: number; y: number }
}): ReactElement => {
    const ref = useRef<HTMLDivElement>(null);
    const pop_w = 300

    const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

    const updatePosition = (pop_h: number) => {
        const edgeOffset = 20
        const pointOffset = 30
        const w = window.innerWidth
        const h = window.innerHeight
        const pointX = point?.x ?? 0
        const pointY = point?.y ?? 0
        const x = (pointX + pointOffset + pop_w + edgeOffset) > w ? (pointX - pointOffset - pop_w) : (pointX + pointOffset)
        const y = (pointY + pop_h + edgeOffset) > h ? (h - pop_h - edgeOffset) : pointY
        setPosition({ x, y })
    }


    useLayoutEffect(() => {
        if (ref.current) {
            updatePosition(ref.current.offsetHeight);
        }
    }, []);

    return (
        <div ref={ref} className='fixed pointer-events-none bg-white bg-opacity-75 rounded shadow-md p-2 z-50' style={{
            left: position.x,
            top: position.y,
            width: `${pop_w}px`
        }}>
            <CameraInViewDetail asset_slug={properties.asset_slug} onHighlightAsset={undefined} className='py-0' />
            <p className='text-xs text-slate-400 mt-2'><em>Click for full details and media</em></p>
        </div>
    )



}


const HoverViewWrapper = ({ hoverItem }: { hoverItem?: ILayerQueryEvent }): ReactElement => {
    const properties = hoverItem?.data?.feature?.properties ?? undefined
    const point = hoverItem?.data?.windowPoint ?? undefined


    return (
        properties !== undefined && point !== undefined ? (
            <HoverView
                properties={properties as IWebCOOSMapAsset}
                point={point}
            />
        ) : <></>
    )
}



const calcBounds = (features: GeoJSON.Feature[]) => {
    const geometries = features.map(d => d.geometry).filter(g => g !== null && g.type === 'Point' && g.coordinates !== undefined) as { type: 'Point'; coordinates: [number, number] }[]
    const lats = geometries.map(d => d.coordinates[1])
    const lons = geometries.map(d => d.coordinates[0])
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLon = Math.min(...lons)
    const maxLon = Math.max(...lons)

    return {
        minLat,
        maxLat,
        minLon,
        maxLon
    }
}

const LegendViewWrapper = (): ReactElement => {
    return (
        <div className='legend absolute top-2 right-2 bg-white px-4 py-2 rounded shadow-md z-50 flex flex-row gap-4'>
            {
                legendItems.map(item => (
                    <div key={item.id} className='flex flex-row items-center gap-2 mb-1 text-sm'>
                        <p>{item.label} <span className={`w-4 h-4 -mb-0.5 rounded-2xl inline-block ml-1 shadow-2xl ${item.border} border-2 ${item.color}`}></span></p>

                    </div>
                ))
            }
        </div>
    )
}

const CamerasInViewWrapper = ({
    data,
    bounds,
    center,
    onHighlightAsset
}: {
    data: IWebCOOSMapAsset[],
    bounds: ILatLonBounds,
    center: ILatLon,
    onHighlightAsset: (asset: IWebCOOSMapAsset | undefined) => void
}): ReactElement => {

    const dataInView = useMemo(() => {
        if (!bounds || !center) {
            return []
        }

        return data
            .filter(d => {
                const coords = d.asset_location?.coordinates
                if (coords !== undefined) {
                    const lon = coords[0]
                    const lat = coords[1]
                    return lon >= bounds.sw.lon && lon <= bounds.ne.lon && lat >= bounds.sw.lat && lat <= bounds.ne.lat
                }
                return false
            })
            .sort((a, b) => {
                const aCoords = a.asset_location?.coordinates
                const bCoords = b.asset_location?.coordinates
                if (aCoords === undefined || bCoords === undefined) {
                    return 0
                }
                const aLon = aCoords[0]
                const aLat = aCoords[1]
                const bLon = bCoords[0]
                const bLat = bCoords[1]
                const aDistSq = (aLon - center.lon) ** 2 + (aLat - center.lat) ** 2
                const bDistSq = (bLon - center.lon) ** 2 + (bLat - center.lat) ** 2
                return aDistSq - bDistSq
            })
    }, [data, bounds, center])

    return <CamerasInView dataInView={dataInView} onHighlightAsset={onHighlightAsset} />
}

const areMapStatesClose = (a?: IMapState, b?: IMapState): boolean => {
    if (a === b) {
        return true
    }
    if (!a || !b || !a.bounds || !b.bounds || !a.center || !b.center) {
        return false
    }

    const epsilon = 0.001
    return (
        Math.abs(a.center.lat - b.center.lat) < epsilon &&
        Math.abs(a.center.lon - b.center.lon) < epsilon &&
        Math.abs((a.zoom ?? 0) - (b.zoom ?? 0)) < epsilon &&
        Math.abs(a.bounds.sw.lat - b.bounds.sw.lat) < epsilon &&
        Math.abs(a.bounds.sw.lon - b.bounds.sw.lon) < epsilon &&
        Math.abs(a.bounds.ne.lat - b.bounds.ne.lat) < epsilon &&
        Math.abs(a.bounds.ne.lon - b.bounds.ne.lon) < epsilon
    )
}

const CameraInViewDetail = ({
    asset_slug,
    className,
    defaultClassName = 'py-8 flex flex-col gap-2',
    onHighlightAsset
}: {
    asset_slug: string,
    onHighlightAsset?: (asset: IWebCOOSMapAsset | undefined) => void,
    className?: string,
    defaultClassName?: string
}): ReactElement => {

    return (
        <CameraDetail
            slug={asset_slug}
            View={({
                detail,
                summary,
                stillImageService,
                isLive
            }): ReactElement => {
                const legendItem = getLegendItemForAsset(summary)
                return (
                    <div className={utils.makeClassName({
                        defaultClassName,
                        className
                    })}
                        onMouseEnter={() => {
                            onHighlightAsset?.(summary)
                        }}
                        onMouseLeave={() => {
                            onHighlightAsset?.(undefined)
                        }}
                    >
                        <div className='aspect-video bg-slate-200 shadow-md relative'
                        >

                            <Link to={`/cameras/${summary.asset_slug}`}>
                                {
                                    isLive && stillImageService !== null
                                        ? <LatestImage
                                            asset={summary}
                                            service={stillImageService}
                                            assetLabel={detail.label}
                                            serviceSlug={stillImageService.data.common.slug}
                                        />
                                        : <ImageLoader url={detail.thumbnail} alt={detail.label} loading='lazy' className="w-full h-auto" />
                                }
                            </Link>

                        </div>
                        <Link to={`/cameras/${summary.asset_slug}`} className='text-md font-bold text-primary hover:underline'>
                            {detail.label}
                        </Link>
                        <div className='text-xs text-gray-600 flex flex-row gap-2 items-center'>
                            <span className={`rounded-4xl w-3 h-3 ${legendItem?.border} ${legendItem?.color}`}></span>
                            {ucFirst(summary.asset_disposition_label)}
                        </div>


                    </div>
                )
            }}
        />
    )

}

const CamerasInView = ({
    dataInView,
    onHighlightAsset,
}: {
    dataInView: IWebCOOSMapAsset[],
    onHighlightAsset: (asset: IWebCOOSMapAsset | undefined) => void,
}): ReactElement => {
    return (
        <div className='h-full w-[300px] overflow-y-auto bg-white divide-y z-30 flex flex-col'>
            {
                dataInView.length === 0 && <div className='text-center p-10'>No Cameras in View</div>
            }
            {
                dataInView.map(d => {
                    return <CameraInViewDetail
                        key={d.asset_slug}
                        asset_slug={d.asset_slug}
                        onHighlightAsset={onHighlightAsset}
                    />

                })
            }
        </div>
    )
}

const LoadedMap = ({
    // id,
    data,
    featureSort,
    //SelectView = SelectViewWrapper,
    HoverView = HoverViewWrapper,
    LegendView = LegendViewWrapper,
    center,
    zoom,
    stylePointFn = (_asset: IWebCOOSMapAsset, defaultProps: Record<string, unknown>) => {
        return defaultProps

    },
    styleWedgeFn = (_asset: IWebCOOSMapAsset, defaultProps: Record<string, unknown>) => {
        return defaultProps
    },
    includeCamerasInView = false,
    CamerasView = CamerasInViewWrapper
}: IMapViewProps): ReactElement => {
    const navigate = useNavigate()
    const [isMobile] = useAtom(isMobileAtom)
    const makeGeojson = useCallback((): GeoJSON.Feature[] => {
        const features = data
            .filter(d => d.asset_location !== null && Array.isArray(d.asset_location?.coordinates) && d.asset_location.coordinates.length === 2)
            .map(d => {
                const legendItem = legendItems.find(item => item.filter(d))
                const defaultProps = {
                    color: legendItem?.hexColor ?? legendItem?.color,
                    stroke: legendItem?.hexStroke ?? '#FFF',
                    'stroke-width': d.asset_disposition_slug === 'up' ? 2 : 1,
                    'stroke-width-hovered': 4,
                    'point-color-hovered': '#FFF',
                    'point-radius': d.asset_disposition_slug === 'up' ? 6 : 4,
                    'point-radius-hovered': 10,
                }
                return {
                    type: "Feature",
                    properties: {
                        ...stylePointFn(d, defaultProps),
                        ...d,
                        id: d.asset_slug,
                        sortOrder: legendItem?.sortOrder
                    },
                    geometry: {
                        type: "Point",
                        coordinates: d.asset_location!.coordinates as [number, number]
                    }
                }
            }) as GeoJSON.Feature[]
        if (featureSort !== undefined) {
            features.sort((a, b) => featureSort(a.properties as IWebCOOSMapAsset, b.properties as IWebCOOSMapAsset))
        } else {
            features.sort((a, b) => (a?.properties?.sortOrder ?? 0) - (b?.properties?.sortOrder ?? 0))
        }
        return features

    }, [data, featureSort, stylePointFn])

    const makePolygonGeojson = useCallback((): GeoJSON.Feature[] => {
        const features = data
            .filter(d => d.asset_wedge !== null && d.asset_wedge !== undefined)
            .map(d => {
                const defaultProps = {
                    stroke: '#FFF',
                    'stroke-width': 2,
                    color: '#FFF',
                    opacity: .5,
                }
                return {
                    type: 'Feature',
                    properties: {
                        ...styleWedgeFn(d, defaultProps),
                        ...d,
                        id: `wedge-${d.asset_slug}`
                    },
                    geometry: d.asset_wedge as GeoJSON.Polygon
                }
            }) as GeoJSON.Feature[]
        if (featureSort !== undefined) {
            features.sort((a, b) => featureSort(a.properties as IWebCOOSMapAsset, b.properties as IWebCOOSMapAsset))
        }
        return features
    }, [data, featureSort, styleWedgeFn])

    const pointsGeoJSON = useMemo(() => makeGeojson(), [makeGeojson])
    const pointFeaturesById = useMemo(() => Object.fromEntries(pointsGeoJSON.map(f => [f.properties?.id, f])), [pointsGeoJSON])
    const wedgesGeoJSON = useMemo(() => makePolygonGeojson(), [makePolygonGeojson])
    //const wedgeFeaturesById = Object.fromEntries(wedgesGeoJSON.map(f => [f.properties?.id, f]))

    const [hoverItem, setHoverItem] = useState<ILayerQueryEvent | undefined>(undefined)
    const [map, setMap] = useState<IMap | undefined>(undefined)
    const [mapState, setMapState] = useState<IMapState | undefined>(undefined)
    const deferredMapState = useDeferredValue(mapState)
    const mapViewRafIdRef = useRef<number | null>(null)
    const pendingMapStateRef = useRef<IMapState | undefined>(undefined)
    useEffect(() => {
        if (map !== undefined) {
            if (map !== undefined) {
                const layersById = Object.fromEntries((map.layers ?? []).map(l => [l.id, l]))
                layers.forEach(l => {
                    layersById[l.id]?.implementation?.unhighlightSelectedFeature()
                })
            }
            map.onMapViewChange((e: IMapStateEvent): void => {
                if (e?.data?.mapState !== undefined) {
                    pendingMapStateRef.current = e.data.mapState
                    if (mapViewRafIdRef.current !== null) {
                        return
                    }
                    mapViewRafIdRef.current = window.requestAnimationFrame(() => {
                        mapViewRafIdRef.current = null
                        setMapState(prev => {
                            const next = pendingMapStateRef.current
                            return areMapStatesClose(prev, next) ? prev : next
                        })
                    })
                }
            })
        }
        return () => {
            if (mapViewRafIdRef.current !== null) {
                window.cancelAnimationFrame(mapViewRafIdRef.current)
                mapViewRafIdRef.current = null
            }
        }
    }, [map])
    const layers: IGeoJSONLayerProps[] = useMemo(() => [
        {
            id: 'assets',
            type: 'geoJson',
            options: {
                geoJson: pointsGeoJSON,
                locationIdProperty: 'id'
            },
            label: '',
            zIndex: 10,
            isBaseLayer: false,
            onMouseOver: (e: ILayerQueryEvent) => {
                if (e?.data?.feature?.properties !== undefined) {
                    setHoverItem(e)
                } else {
                    setHoverItem(undefined)
                }
            },
            onMouseOut: () => {
                setHoverItem(undefined)
            },
            onSelect: function (e: ILayerQueryEvent) {
                if (e?.data?.feature) {
                    navigate(`/cameras/${e.data.feature.properties?.asset_slug}`)
                }
            }

        },
        {
            id: 'wedges',
            type: 'geoJson',
            options: {
                geoJson: wedgesGeoJSON,
                locationIdProperty: 'id'
            },
            label: '',
            zIndex: 0,
            isBaseLayer: false

        }
    ], [navigate, pointsGeoJSON, wedgesGeoJSON])
    useEffect(() => {
        if (map !== undefined) {
            const newGeojson = makeGeojson()
            const newPolygonGeojson = makePolygonGeojson()
            //map.removeLayer('assets')
            //map.removeLayer('wedges')
            const layersById = Object.fromEntries((map.layers ?? []).map(l => [l.id, l]))
            if (layersById.assets) {
                layersById.assets.implementation?.updateOptions({
                    geoJson: newGeojson
                })
            }
            if (layersById.wedges) {
                layersById.wedges.implementation?.updateOptions({
                    geoJson: newPolygonGeojson
                })
            }
            if (center) {
                map.setCenter(center)
                if (zoom) {
                    map.setZoom(zoom)
                }
            } else {
                const { minLon, maxLon, minLat, maxLat } = calcBounds(newGeojson)
                map.setBounds(
                    new LatLonBounds({
                        sw: { lat: minLat, lon: minLon },
                        ne: { lat: maxLat, lon: maxLon }
                    }),
                    {
                        padding: {
                            top: 100,
                            bottom: 100,
                            left: 100,
                            right: 100
                        }
                    }
                )
            }
        }
    }, [data])

    const props: { center?: ILatLon, zoom?: number } = {}
    if (center) {
        props.center = center
    }
    if (zoom) {
        props.zoom = zoom
    }
    const mapIsReady = !includeCamerasInView || (mapState?.bounds !== undefined && mapState?.center !== undefined)
    return <>
        {
            !mapIsReady && <Loader className='pt-20' />
        }
        <div className={`relative h-full w-full flex flex-row${mapIsReady ? ' visible' : ' invisible'}`}>
            {
                includeCamerasInView && !isMobile &&
                <div className='w-[300px] h-full'>{
                    deferredMapState?.bounds !== undefined && deferredMapState?.center !== undefined &&
                    <CamerasView
                        data={data}
                        bounds={deferredMapState.bounds}
                        center={deferredMapState.center}
                        onHighlightAsset={(asset) => {
                            if (map !== undefined) {
                                const layersById = Object.fromEntries((map.layers ?? []).map(l => [l.id, l]))
                                const assetsLayer = layersById.assets
                                if (assetsLayer !== undefined) {
                                    const feature = pointFeaturesById[asset?.asset_slug ?? '']
                                    if (feature !== undefined) {
                                        assetsLayer.implementation?.highlightHoveredFeature(feature, { dispatchEvent: false })
                                    } else {
                                        assetsLayer.implementation?.unhighlightHoveredFeature()
                                    }
                                    console.log(assetsLayer)
                                    //assetsLayer.implementation?.highlightHoveredFeature(asset, { dispatchEvent: false })
                                }
                            }
                            console.log('highlighting asset', asset)
                        }
                        } />
                }
                </div>
            }
            <div className='h-full relative flex grow'>
                <MapLoader
                    Loader={<Loader className='pt-20' />}
                    mapLibraryKey='mapbox'
                    layers={layers}
                    className='h-full'
                    height='100%'
                    baseLayerKey='mb_bathymetry'
                    setState={setMap}
                    maxZoom={10}
                    minZoom={0}
                    {...props}
                    onMapLoaded={(e) => {
                        if (e.data?.map === undefined) return
                        if (center) {
                            e.data.map.setCenter(center)
                            if (zoom) {
                                e.data.map.setZoom(zoom)
                            }
                        } else {
                            const {
                                minLon, maxLon, minLat, maxLat
                            } = calcBounds(pointsGeoJSON)
                            e.data.map.setBounds(
                                new LatLonBounds({
                                    sw: { lat: minLat, lon: minLon },
                                    ne: { lat: maxLat, lon: maxLon }
                                }),
                                {
                                    padding: {
                                        top: 100,
                                        bottom: 100,
                                        left: 100,
                                        right: 100
                                    }
                                }
                            )
                        }
                        /* if(e?.data?.map?.layers !== undefined){
                            e.data.map.layers.forEach(l => {
                                e?.data?.map.removeLayer(l.id)
                            })
                        }
                        layers.forEach(l => {
                            e?.data?.map.addLayer(l)
                        }) */
                    }}

                />
            </div>
            {
                LegendView && <LegendView />
            }
            {
                HoverView && <HoverView hoverItem={hoverItem} />
            }
            {
                // SelectView && <SelectView selectedItem={selectedItem} setSelectedItem={setSelectItem} />
            }
        </div >
    </>
}

export default LoadedMap